import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, DollarSign, Package, Trash2, X } from 'lucide-react';
import AccountCodeManager from '../../managers/AccountCodeManager';
import { AccountCode } from '../../interfaces/AccountCodeInterface';
import { getProductionPayErrorMessage, splitDay } from '../../api/productionPay';

/**
 * Wallcraft production totals — "Edit account codes & hours" (design §6).
 *
 * Re-codes or splits one (employee · local day · job) across account codes. Rendered
 * INLINE under `.etrc` like MiscTaskCodeModal (no portal), so the console's tokens and
 * the flag-gated `.etrc-prod-*` block style it; Escape closes via a window listener.
 *
 * The job is display-only — moving hours between jobs is a separate audited action and
 * is out of scope. The punched total is structurally invariant, so Save stays disabled
 * until the split reads 0.00 off. Amounts and PO #s are preserved SERVER-side; this
 * modal only shows the values that will carry over.
 */

export interface ProductionSplitRow {
  accountCodeID: number | null;
  /** Display name of the code the hours sit on today ("Tape"). */
  accountCodeName: string;
  altAccountCodeID: string;
  hours: number;
  /** The Total $ that will carry over onto this code; null = nothing entered. */
  amount: number | null;
  poNumber: string | null;
}

export interface ProductionSplitTarget {
  employeeID: number;
  payPeriodID: number;
  /** `YYYY-MM-DD`, the punches' LOCAL day. */
  entryDate: string;
  dow: string;
  dateLabel: string;
  projectID: number;
  altProjectID: string | null;
  projectName: string;
  /** "02100 · Bldg A L2" — the phase sub-line, blank when the job has no phase. */
  phaseName: string;
  /** Punched hours on this (day, job). The split has to add back up to it. */
  punchedHours: number;
  /** `config.productionPay.poAccountCodeID` — never matched by name (Rule 6). */
  poAccountCodeID: number;
  rows: ProductionSplitRow[];
}

export interface ProductionSplitModalProps {
  target: ProductionSplitTarget;
  /** False for role-OFF employees (no `RequireProductionPay`): they never owe a
   *  total, so the Total $ column is hidden and only the PO # chip survives. */
  showAmounts?: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const PROD_COLOR = '#0e7c86';

interface EditRow extends ProductionSplitRow {
  /** Free text while the field is being typed; parsed on every keystroke for the meter. */
  hoursText: string;
}

function h2(n: number): string {
  return n.toFixed(2);
}

function money(n: number | null): string {
  if (n == null) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const ProductionSplitModal: React.FC<ProductionSplitModalProps> = ({ target, showAmounts = true, onClose, onSaved }) => {
  const [accountCodes, setAccountCodes] = useState<AccountCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<EditRow[]>(
    () => target.rows.map((r) => ({ ...r, hoursText: h2(r.hours) }))
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const codes = await AccountCodeManager.getAccountCodes();
        if (!cancelled) setAccountCodes(codes ?? []);
      } catch {
        if (!cancelled) setLoadError('Could not load account codes. Close and try again.');
      } finally {
        if (!cancelled) setCodesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  /** Active codes, plus any code already on the day so a deactivated one stays selectable. */
  const options = useMemo(() => {
    const active = accountCodes
      .filter((c) => c.active !== false)
      .sort((a, b) => (a.altAccountCodeID ?? '').localeCompare(b.altAccountCodeID ?? ''));
    const extras = target.rows
      .map((r) => r.accountCodeID)
      .filter((id): id is number => id != null && !active.some((c) => c.accountCodeID === id))
      .map((id) => accountCodes.find((c) => c.accountCodeID === id))
      .filter((c): c is AccountCode => c != null);
    return [...extras, ...active];
  }, [accountCodes, target.rows]);

  const sum = rows.reduce((s, r) => s + (parseFloat(r.hoursText) || 0), 0);
  const diff = Math.round((target.punchedHours - sum) * 100) / 100;
  const balanced = Math.abs(diff) <= 0.005;

  const hasZeroRow = rows.some((r) => !((parseFloat(r.hoursText) || 0) > 0));
  const codeIDs = rows.map((r) => r.accountCodeID);
  const hasMissingCode = codeIDs.some((id) => id == null);
  const hasDuplicateCode = new Set(codeIDs.filter((id) => id != null)).size
    !== codeIDs.filter((id) => id != null).length;
  const structureOk = !hasZeroRow && !hasMissingCode && !hasDuplicateCode;

  const canSave = balanced && structureOk && !saving && !loadError && !codesLoading;

  const setRow = (i: number, patch: Partial<EditRow>) => {
    setRows((prev) => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  };

  const removeRow = (i: number) => setRows((prev) => prev.filter((_, k) => k !== i));

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        accountCodeID: null,
        accountCodeName: '',
        altAccountCodeID: '',
        hours: 0,
        hoursText: '0.00',
        amount: null,
        poNumber: null,
      },
    ]);
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await splitDay({
        employeeID: target.employeeID,
        payPeriodID: target.payPeriodID,
        entryDate: target.entryDate,
        projectID: target.projectID,
        segments: rows.map((r) => ({
          accountCodeID: r.accountCodeID as number,
          hours: Math.round((parseFloat(r.hoursText) || 0) * 100) / 100,
        })),
      });
      if (!result.success) {
        setSaveError(result.errorMessage ?? 'Could not save the split.');
        return;
      }
      await onSaved();
      onClose();
    } catch (err: unknown) {
      setSaveError(getProductionPayErrorMessage(err, 'Could not save the split.'));
    } finally {
      setSaving(false);
    }
  };

  const error = loadError ?? saveError;

  return (
    <div className="etrc-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="etrc-mw-modal etrc-prod-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit account codes and hours"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="etrc-mw-header etrc-prod-mhead">
          <div className="etrc-mw-icon-wrap etrc-prod-micon">
            <Package size={22} color={PROD_COLOR} />
          </div>
          <div>
            <div className="etrc-mw-title">Edit account codes &amp; hours</div>
            <div className="etrc-mw-subtitle">
              {target.dow}, {target.dateLabel} · {h2(target.punchedHours)} hrs punched
            </div>
          </div>
          <button type="button" className="etrc-mw-close" onClick={onClose} disabled={saving} title="Close">
            <X size={18} />
          </button>
        </div>

        <div className="etrc-mw-body">
          <p className="etrc-prod-mlead">
            Re-code the day or split it across account codes without leaving the week grid. The job
            the hours sit on cannot be changed here, and the punched total is fixed at{' '}
            <b>{h2(target.punchedHours)} hrs</b> — the split has to add up before it can be saved.
          </p>

          <div className="etrc-table-wrap etrc-prod-etwrap">
            <table className="etrc-prod-etable">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Account code</th>
                  <th style={{ width: 96 }}>Hours</th>
                  {showAmounts ? <th style={{ width: 130 }}>Total $</th> : <th style={{ width: 0 }} aria-hidden="true" />}
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const isPO = row.accountCodeID === target.poAccountCodeID;
                  return (
                    <tr key={i}>
                      <td>
                        <span className="etrc-prod-ejob">
                          {target.altProjectID && <span className="no">#{target.altProjectID}</span>}
                          <span className="nm">{target.projectName}</span>
                          {target.phaseName && <span className="ph">{target.phaseName}</span>}
                        </span>
                      </td>
                      <td>
                        <select
                          className="etrc-prod-eselect"
                          value={row.accountCodeID ?? ''}
                          disabled={codesLoading || !!loadError || saving}
                          onChange={(e) => setRow(i, {
                            accountCodeID: e.target.value === '' ? null : Number(e.target.value),
                          })}
                        >
                          {codesLoading && <option value="">Loading codes…</option>}
                          {!codesLoading && row.accountCodeID == null && (
                            <option value="">Select an account code…</option>
                          )}
                          {options.map((c) => (
                            <option key={c.accountCodeID} value={c.accountCodeID}>
                              {c.altAccountCodeID} · {c.name}{c.active === false ? ' (inactive)' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="etrc-prod-ehours"
                          inputMode="decimal"
                          value={row.hoursText}
                          disabled={saving}
                          onChange={(e) => setRow(i, { hoursText: e.target.value })}
                          onBlur={(e) => {
                            const n = parseFloat(e.target.value);
                            setRow(i, { hoursText: isNaN(n) ? '0.00' : h2(Math.round(n * 100) / 100) });
                          }}
                          aria-label="Hours on this account code"
                        />
                      </td>
                      {showAmounts ? (
                        <td className="etrc-prod-eamt">
                          <span className={row.amount == null ? 'na' : undefined}>{money(row.amount)}</span>
                          {isPO && (
                            <span className="po">
                              PO{' '}
                              {row.poNumber
                                ? `#${row.poNumber}`
                                : <span className="req">required</span>}
                            </span>
                          )}
                        </td>
                      ) : (
                        // Role-OFF: no Total $ column, but the PO # obligation exists
                        // for every role — the chip rides along in a slim cell.
                        <td className="etrc-prod-eamt" style={{ width: 0 }}>
                          {isPO && (
                            <span className="po">
                              PO{' '}
                              {row.poNumber
                                ? `#${row.poNumber}`
                                : <span className="req">required</span>}
                            </span>
                          )}
                        </td>
                      )}
                      <td>
                        {rows.length > 1 && (
                          <button
                            type="button"
                            className="etrc-prod-erm"
                            title="Remove this code"
                            aria-label="Remove this account code"
                            disabled={saving}
                            onClick={() => removeRow(i)}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="etrc-prod-addrow">
            <button type="button" className="etrc-btn small" onClick={addRow} disabled={saving}>
              <Package size={13} />Add another account code
            </button>
          </div>

          <div className={`etrc-prod-meter${balanced ? '' : ' off'}`}>
            {balanced ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            <span>
              {balanced
                ? <>Split accounts for all <b>{h2(target.punchedHours)} hrs</b></>
                : <>Split is off by <b>{diff > 0 ? '+' : ''}{h2(diff)} hrs</b> — must reach 0.00 to save</>}
            </span>
          </div>

          {balanced && !structureOk && (
            <div className="etrc-prod-mwarn">
              <AlertTriangle size={14} />
              <span>
                Every row needs an account code with hours above 0.00, and an account code can only
                appear once.
              </span>
            </div>
          )}

          <div className="etrc-prod-mnote">
            {showAmounts ? (
              <>
                Changing hours re-derives base pay for that code, so a total that was fine can fall
                below base — it is flagged, never silently changed. Removing a code drops its total.
                Moving a row off the Purchase Orders code clears its PO #.
              </>
            ) : (
              <>Moving a row off the Purchase Orders code clears its PO #.</>
            )}
          </div>

          {error && (
            <div className="etrc-mtc-error">
              <AlertCircle size={15} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="etrc-mw-footer">
          <button type="button" className="etrc-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="etrc-btn primary" onClick={handleSave} disabled={!canSave}>
            <DollarSign size={15} />{saving ? 'Saving…' : 'Save split'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductionSplitModal;
